/**
 * Results Page Component (Phase 6: Email Finder)
 * 
 * This is the main results view that combines:
 * - Results statistics header (including email stats)
 * - Filter panel sidebar
 * - Results table with email/social columns
 * - Download buttons
 * 
 * It manages the filtered results state and handles filter application.
 * Passes premium status to child components for email feature gating.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import ResultsTable from './ResultsTable';
import FilterPanel from './FilterPanel';
import DownloadButtons from './DownloadButtons';
import { getUserPlan, getResults, findEmail } from '../utils/api';
import { COUNTRIES } from '../data/countries';
import { Link } from 'react-router-dom';

// Track which jobs have already had auto email finder run
// This persists across component mounts/unmounts during the same session
const completedEmailJobs = new Set();

function ResultsPage({ jobId, results, searchParams, onBack }) {
  // ═══════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════
  
  // Current leads being displayed (may be filtered)
  const [leads, setLeads] = useState(results?.leads || []);
  
  // Total count before filtering
  const [totalCount, setTotalCount] = useState(results?.total || 0);
  
  // Count after filtering
  const [filteredCount, setFilteredCount] = useState(results?.filtered || 0);
  
  // Statistics about the results
  const [statistics, setStatistics] = useState(results?.statistics || {});
  
  // Whether filters are being applied
  const [isFiltering, setIsFiltering] = useState(false);
  
  // Currently applied filters
  const [activeFilters, setActiveFilters] = useState({});
  
  // Whether to show the filter panel (mobile toggle)
  const [showFilters, setShowFilters] = useState(false);

  // Plan Statistics
  const [planStats, setPlanStats] = useState(null);

  // Is the user premium?
  const isPremium = planStats?.plan === 'premium';

  // Auto Email Finder Progress
  const [autoEmailProgress, setAutoEmailProgress] = useState({ 
    running: false, 
    current: 0, 
    total: 0, 
    found: 0 
  });

  // Ref to track if auto-finder has been initiated for this results set
  const autoFinderInitiatedRef = useRef(false);
  
  // Ref to handle cleanup on unmount
  const autoFinderCancelledRef = useRef(false);

  // Sync leads state when results prop changes (e.g. navigating back from History)
  useEffect(() => {
    if (results?.leads) {
      setLeads(results.leads);
      setTotalCount(results.total || 0);
      setFilteredCount(results.filtered || 0);
      setStatistics(results.statistics || {});
    }
  }, [results]);

  useEffect(() => {
    const fetchPlanStats = async () => {
      try {
        const stats = await getUserPlan();
        setPlanStats(stats);
      } catch (err) {
        console.error("Failed to load plan stats", err);
      }
    };
    fetchPlanStats();
  }, []);

  // HANDLERS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Handle on-demand email find result updating a single lead
   */
  const handleLeadEmailUpdate = useCallback((leadId, emailData) => {
    setLeads(prev => prev.map(lead => {
      if (lead.id === leadId) {
        return {
          ...lead,
          email: emailData.email,
          email_confidence: emailData.email_confidence,
          social_media: emailData.social_media || lead.social_media
        };
      }
      return lead;
    }));
  }, []);

  /**
   * Auto Email Finder - runs automatically for premium users
   * Finds emails for all leads with websites but no emails yet
   */
  const startAutoEmailFinder = useCallback(async () => {
    const BATCH_SIZE = 3;
    const BATCH_DELAY = 100;

    // Get snapshot of leads that need emails
    const leadsWithWebsite = leads.filter(l => l.website && !l.email);
    
    if (leadsWithWebsite.length === 0) return;
    
    // Set running state
    setAutoEmailProgress({ 
      running: true, 
      current: 0, 
      total: leadsWithWebsite.length, 
      found: 0 
    });
    
    let foundCount = 0;
    
    // Process leads in batches of BATCH_SIZE
    for (let i = 0; i < leadsWithWebsite.length; i += BATCH_SIZE) {
      // Check if auto-finder was cancelled
      if (autoFinderCancelledRef.current) break;
      
      const batch = leadsWithWebsite.slice(i, i + BATCH_SIZE);
      
      // Process all leads in the batch simultaneously
      const results = await Promise.allSettled(
        batch.map(async (lead) => {
          try {
            const result = await findEmail(lead.place_id, lead.website);
            if (result.email) {
              foundCount++;
              handleLeadEmailUpdate(lead.id, {
                email: result.email,
                email_confidence: result.confidence,
                social_media: result.social_media
              });
            }
            return result;
          } catch (error) {
            console.error(`Error finding email for lead ${lead.id}:`, error);
            return null;
          }
        })
      );
      
      // Update progress after batch completes
      setAutoEmailProgress(prev => ({
        ...prev,
        current: Math.min(i + BATCH_SIZE, leadsWithWebsite.length),
        found: foundCount
      }));
      
      // Add delay between batches
      if (i + BATCH_SIZE < leadsWithWebsite.length) {
        await new Promise(r => setTimeout(r, BATCH_DELAY));
      }
    }
    
    // Mark as complete
    setAutoEmailProgress(prev => ({
      ...prev,
      running: false
    }));

    // Mark this job's email finding as completed
    if (jobId) {
      completedEmailJobs.add(jobId);
    }
  }, [leads, handleLeadEmailUpdate, jobId]);

  /**
   * Auto-start email finder for premium users when results load
   */
  useEffect(() => {
    // Check if auto-finder has already been run for this specific job
    if (!jobId) return;
    if (completedEmailJobs.has(jobId)) return;
    if (autoFinderInitiatedRef.current) return;

    // Only start if all conditions are met
    if (
      isPremium &&
      leads.length > 0 &&
      autoEmailProgress.current === 0 &&
      leads.filter(l => l.website && !l.email).length > 0
    ) {
      autoFinderInitiatedRef.current = true;
      completedEmailJobs.add(jobId);
      startAutoEmailFinder();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium, leads.length, jobId]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      autoFinderCancelledRef.current = true;
    };
  }, []);

  /**
   * Handle filter application - Client-side filtering
   * Called when user clicks "Apply Filters" in the FilterPanel
   */
  const handleApplyFilters = (filters) => {
    setIsFiltering(true);
    setActiveFilters(filters);
    
    try {
      // Check if any filters are actually set
      const hasActiveFilters = Object.values(filters).some(v => v !== null && v !== false);
      
      if (!hasActiveFilters) {
        // No filters, show all results
        setLeads(results.leads);
        setFilteredCount(results.total);
        setStatistics(results.statistics);
      } else {
        // Apply filters locally
        const filteredLeads = results.leads.filter(lead => {
          // Filter: no website
          if (filters.no_website && lead.website) {
            return false;
          }
          
          // Filter: has website
          if (filters.has_website && !lead.website) {
            return false;
          }
          
          // Filter: has phone
          if (filters.has_phone && !lead.phone) {
            return false;
          }
          
          // Filter: minimum rating
          if (filters.min_rating && (!lead.rating || lead.rating < filters.min_rating)) {
            return false;
          }
          
          // Filter: minimum reviews
          if (filters.min_reviews && (!lead.total_ratings || lead.total_ratings < filters.min_reviews)) {
            return false;
          }
          
          return true;
        });
        
        setLeads(filteredLeads);
        setFilteredCount(filteredLeads.length);
        
        // Recalculate statistics based on filtered leads
        const stats = {
          with_phone: filteredLeads.filter(l => l.phone).length,
          with_website: filteredLeads.filter(l => l.website).length,
          with_email: filteredLeads.filter(l => l.email).length,
          without_website: filteredLeads.filter(l => !l.website).length
        };
        setStatistics(stats);
      }
    } catch (error) {
      console.error('Filter error:', error);
      alert('Failed to apply filters. Please try again.');
    } finally {
      setIsFiltering(false);
      // Close filter panel on mobile after applying
      setShowFilters(false);
    }
  };

  /**
   * Handle clearing all filters
   */
  const handleClearFilters = () => {
    setActiveFilters({});
    setLeads(results.leads);
    setFilteredCount(results.total);
    setStatistics(results.statistics);
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Format the business type for display
   */
  const formatBusinessType = (type) => {
    if (!type) return 'Business';
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  const emailCount = leads.filter(l => l.email).length;

  return (
    <div className="animate-fade-in">
      {/* Results Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            {/* Back Button */}
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors text-sm mb-3"
            >
              <span>← New Search</span>
            </button>
            <h2 className="text-2xl sm:text-3xl font-bold text-white">
              {COUNTRIES[searchParams.country]?.flag || '🌍'} Search <span className="text-gradient">Results</span>
            </h2>
            <p className="text-gray-400 mt-1">
              {formatBusinessType(searchParams.business_type)} in {searchParams.city}, {searchParams.country || "India"}
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:items-end space-y-2 mt-4 sm:mt-0">
            <div className="flex items-center space-x-3">
              {/* Mobile Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="sm:hidden btn-secondary flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span>Filters</span>
              </button>
              
              {/* Download Buttons */}
              {planStats?.plan === 'basic' ? (
                <div className="flex items-center space-x-2 text-sm bg-slate-800 border border-white/10 px-3 py-2 rounded-lg">
                  <span className="text-gray-400">🔒</span>
                  <Link to="/pricing" className="text-blue-400 hover:text-blue-300 font-medium">
                    Upgrade to Standard to export leads
                  </Link>
                </div>
              ) : (
                <DownloadButtons jobId={jobId} activeFilters={activeFilters} />
              )}
            </div>
            
            {planStats && (
              <div className="text-xs text-gray-500 font-medium bg-slate-800/50 px-3 py-1 rounded-full border border-white/10/50 self-end">
                Used {planStats.leads_used_today}/{planStats.leads_per_day} leads today
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
        <div className="card p-4">
          <p className="text-2xl font-bold text-white">{filteredCount}</p>
          <p className="text-sm text-gray-400">Total Leads</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-green-400">{statistics.with_phone || 0}</p>
          <p className="text-sm text-gray-400">With Phone</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-blue-400">{statistics.with_website || 0}</p>
          <p className="text-sm text-gray-400">With Website</p>
        </div>
        {/* Phase 6: Email count card */}
        <div className="card p-4">
          <div className="flex items-center space-x-2">
            <p className="text-2xl font-bold text-purple-400">{isPremium ? emailCount : '🔒'}</p>
          </div>
          <p className="text-sm text-gray-400">
            {isPremium ? 'With Email' : (
              <Link to="/pricing" className="text-purple-400 hover:text-purple-300 transition-colors">
                Emails (Premium)
              </Link>
            )}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-amber-400">{statistics.without_website || 0}</p>
          <p className="text-sm text-gray-400">No Website</p>
        </div>
      </div>

      {/* Low Results Info Banner */}
      {searchParams.limit && searchParams.limit > totalCount * 1.5 && totalCount > 0 && (
        <div className="mb-4 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center space-x-3 animate-fade-in">
          <span className="text-lg">ℹ️</span>
          <p className="text-sm text-blue-300">
            <span className="font-medium">You requested {searchParams.limit} leads, but {totalCount} unique results were found.</span>{' '}
            Google Places typically returns 60-120 unique businesses per area. Try searching in a larger city or different location for more results.
          </p>
        </div>
      )}

      {/* Auto Email Finder Progress Banner */}
      {isPremium && autoEmailProgress.running && leads.length > 0 && (
        <div className="mb-4 p-4 rounded-xl bg-purple-500/10 border border-purple-500/20 animate-fade-in">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-5 h-5 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin flex-shrink-0" />
            <p className="text-sm text-purple-300">
              <span className="font-medium">Auto Email Finder Running...</span>{' '}
              Finding emails for {autoEmailProgress.current}/{autoEmailProgress.total} leads ({autoEmailProgress.found} found so far)
            </p>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-300"
              style={{ width: `${(autoEmailProgress.current / autoEmailProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Email Finder tip banner (Premium users without emails yet, not running auto-finder) */}
      {isPremium && !autoEmailProgress.running && emailCount === 0 && autoEmailProgress.current === 0 && leads.length > 0 && (
        <div className="mb-4 p-3 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center space-x-3 animate-fade-in">
          <span className="text-lg">💡</span>
          <p className="text-sm text-purple-300">
            <span className="font-medium">Email Finder Available</span> — Click the 'Find' button next to any lead to discover their email address from their website.
          </p>
        </div>
      )}

      {/* Found X emails banner */}
      {isPremium && !autoEmailProgress.running && emailCount > 0 && (
        <div className="mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center space-x-3 animate-fade-in">
          <span className="text-lg">📧</span>
          <p className="text-sm text-emerald-300">
            <span className="font-medium">Found {emailCount} email{emailCount !== 1 ? 's' : ''}</span> out of {leads.length} leads. Click 'Find' on more leads to discover additional emails.
          </p>
        </div>
      )}

      {/* Email scan completion banner */}
      {isPremium && !autoEmailProgress.running && autoEmailProgress.current > 0 && autoEmailProgress.total > 0 && (
        <div className="mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center space-x-3 animate-fade-in">
          <span className="text-lg">✅</span>
          <p className="text-sm text-emerald-300">
            <span className="font-medium">Email scan complete!</span> — Found {autoEmailProgress.found} emails out of {autoEmailProgress.total} leads with websites. You can still click 'Find' on individual leads to retry.
          </p>
        </div>
      )}

      {/* Main Content: Filters + Table */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Filter Panel - Desktop (always visible) / Mobile (toggleable) */}
        <div className={`
          lg:w-72 flex-shrink-0
          ${showFilters ? 'block' : 'hidden lg:block'}
        `}>
          <FilterPanel
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
            isLoading={isFiltering}
            statistics={statistics}
          />
        </div>

        {/* Results Table */}
        <div className="flex-1 min-w-0">
          {leads.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-white mb-2">No Leads Found</h3>
              <p className="text-gray-400 mb-6">Try different search keywords or expand your location.</p>
              <Link to="/" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 py-3 inline-block font-medium transition-colors">
                New Search
              </Link>
            </div>
          ) : (
            <>
              <ResultsTable 
                leads={leads} 
                isLoading={isFiltering}
                isPremium={isPremium}
                onLeadEmailUpdate={handleLeadEmailUpdate}
              />
              
              {/* Results Summary */}
              {filteredCount > 0 && (
                <div className="mt-4 text-center text-sm text-gray-500">
                  Showing {leads.length} of {filteredCount} leads
                  {Object.keys(activeFilters).length > 0 && (
                    <span className="ml-2">
                      (filtered from {totalCount} total)
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResultsPage;
